import { FC } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import cn from 'classnames';

import FormCheckbox from '@components/FormCheckbox/FormCheckbox';
import FormSelect from '@components/FormSelect/FormSelect';
import FormTextField from '@components/FormTextField/FormTextField';
import { PreviewField } from '@components/PreviewField/PreviewField';
import useUnsavedChanges from '@hooks/useUnsavedChanges';
import useAthleteRoleGroups from '@store/hooks/useAthleteRoleGroups';

import FormDrugClasses from './FormDrugClasses/FormDrugClasses';
import FormRiskLevels from './FormRiskLevels/FormRiskLevels';
import { IProgramCodeDetailsFormProps } from './ProgramCodeDetailsForm.interface';

import './style.scss';

const ProgramCodeDetailsForm: FC<IProgramCodeDetailsFormProps> = ({
  isNew,
  handleSubmit,
  values,
  hasValidationErrors,
  submitting,
  onCancel,
}) => {
  const { t } = useTranslation();
  const athleteRoleGroups = useAthleteRoleGroups();

  useUnsavedChanges();

  return (
    <div className={cn('program-code-details-form')}>
      <div className="program-code-details-form__fieldset">
        <Field component={FormTextField} label={t('admin.name')} name="name" required />

        <Field component={FormTextField} label={t('admin.code')} name="code" required />
      </div>

      <div className="program-code-details-form__fieldset program-code-details-form__fieldset_checkboxes">
        <Field
          component={FormCheckbox}
          name="canUseDrugInquiry"
          label={t('admin.canUseDrugInquiry')}
          type="checkbox"
        />

        <Field
          component={FormCheckbox}
          name="canUseSupplementInquiry"
          label={t('admin.canUseSupplementInquiry')}
          type="checkbox"
        />

        <Field
          component={FormCheckbox}
          name="canUseEducation"
          label={t('admin.canUseEducation')}
          type="checkbox"
        />
      </div>

      <Field component={FormRiskLevels} name="riskLevels" />

      <div>
        <Field
          component={FormCheckbox}
          name="hasInOutCompBan"
          label={t('admin.useCompitition')}
          type="checkbox"
        />
      </div>

      <div className="program-code-details-form__fieldset">
        <Field component={FormTextField} label={t('admin.bannedText')} name="bannedText" required />
      </div>

      <div className="program-code-details-form__fieldset">
        <Field
          component={FormTextField}
          label={t('admin.notBannedText')}
          name="notBannedText"
          required
        />
      </div>

      <PreviewField
        value={values.medicationNotFoundText}
        label={t('admin.medicationNotFoundText')}
        name="medicationNotFoundText"
      />

      <div className="program-code-details-form__fieldset">
        <Field
          component={FormTextField}
          label={t('admin.emailHeader')}
          name="emailHeader"
          multiline
        />
      </div>

      <div className="program-code-details-form__fieldset">
        <Field
          component={FormTextField}
          label={t('admin.signupEmailHeader')}
          name="signupEmailHeader"
          multiline
        />
      </div>

      <div className="program-code-details-form__fieldset">
        <Field
          component={FormTextField}
          label={t('admin.adminEmailHeader')}
          name="adminEmailHeader"
          multiline
        />
      </div>

      <PreviewField
        value={values.headlineText}
        label={t('admin.headlineText')}
        name="headlineText"
      />

      <PreviewField
        value={values.infoTileText}
        label={t('admin.infoTileText')}
        name="infoTileText"
      />

      <div className="program-code-details-form__fieldset">
        <Field component={FormTextField} label={t('admin.infoPageTitle')} name="infoPageTitle" />
      </div>

      <PreviewField
        value={values.infoPageText}
        label={t('admin.infoPageText')}
        name="infoPageText"
      />

      <div className="program-code-details-form__fieldset program-code-details-form__fieldset_checkboxes">
        <Field
          component={FormCheckbox}
          name="multiLanguage"
          label={t('admin.multiLanguage')}
          type="checkbox"
        />
      </div>

      <div className="program-code-details-form__fieldset">
        <Field
          component={FormSelect}
          label={t('admin.athleteRoleGroup')}
          name="athleteRoleGroupId"
          options={athleteRoleGroups}
          required
        />
      </div>

      <Field component={FormDrugClasses} name="drugClasses" />

      <div className="program-code-details-form__controls">
        <Button variant="outlined" onClick={onCancel}>
          {t('admin.cancel')}
        </Button>

        <Button
          variant="contained"
          disabled={hasValidationErrors || submitting}
          onClick={handleSubmit}
        >
          {isNew ? t('admin.create') : t('admin.save')}
        </Button>
      </div>
    </div>
  );
};

export default ProgramCodeDetailsForm;
